import { useState, useEffect } from "react";
import { Button } from "@component/button";
import { Input } from "@component/input";
import { Label } from "@component/label";
import {
    EyeIcon,
    UploadIcon,
    XIcon,
    PlusIcon,
    RefreshCcw,
    CopyIcon,
} from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { DemoInterviewFormRequest } from "@type/interview";
import { createUpdateDemoInterview, getDemoCompanyDetail, getListAssessment, getAssessmentDetail } from "@service/assessment";
import { useSnackbar } from "@hook/use-snackbar";
import { DemoPreviewDialog } from "./demo-preview-dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@component/select";
import { Assessment, InterviewAssessment } from "@type/assessments";
// import { DemoPreviewDialog } from "@/polymet/components/demo-preview-dialog";

interface Props {
    isEditMode?: boolean;
}

export default function DemoInterviewForm(props: Props) {
    const snackbar = useSnackbar();
    const { alias } = useParams();
    const [formData, setFormData] = useState<DemoInterviewFormRequest>({
        company_name: "",
        alias: "",
        company_logo_preview: "",
        company_logo_interview_preview: "",
        company_logo: null,
        company_logo_interview: null,
        company_color: "#2863EB"
    });

    const [logoAPreviews, setLogoAPreviews] = useState<string | null>(null);
    const [logoBPreviews, setLogoBPreviews] = useState<string | null>(null);
    const [companyLogoFile, setCompanyLogoFile] = useState<File | null>(null);
    const [companyLogoInterviewFile, setCompanyLogoInterviewFile] = useState<File | null>(null);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [, setIsCopied] = useState(false);

    // Assessment state
    const [assessments, setAssessments] = useState<Assessment[]>([]);
    const [selectedInterviewAssessment, setSelectedInterviewAssessment] = useState<{ type: string; id: string; name: string }>({ type: "", id: "", name: "None" });

    // Load assessments
    const loadAssessment = () => {
        getListAssessment().then((resp) => {
            if (resp.status === 200) {
                setAssessments(resp.data.data);
            }
        })
    }

    useEffect(() => {
        loadAssessment()
    }, [])

    // Load existing data when in edit mode
    useEffect(() => {
        if (props.isEditMode && alias) {
            getDemoCompanyDetail(alias || "").then(async (resp) => {
                if (resp.status === 200) {
                    const data = resp.data.data;
                    setFormData({
                        company_name: data.company_name,
                        alias: data.alias,
                        company_logo_preview: data.company_logo,
                        company_logo_interview_preview: data.company_logo_interview,
                        company_logo: null,
                        company_logo_interview: null,
                        company_color: data.company_color
                    });
                    // Set logo previews from existing URLs
                    setLogoAPreviews(data.company_logo_interview);
                    setLogoBPreviews(data.company_logo);

                    // Set selected interview assessment if exists - fetch the assessment to get the name
                    if (data.assessment_id) {
                        try {
                            const assessmentResp = await getAssessmentDetail(data.assessment_id);
                            if (assessmentResp.status === 200) {
                                const assessment = assessmentResp.data.data as InterviewAssessment;
                                setSelectedInterviewAssessment({
                                    type: "Interview",
                                    id: data.assessment_id,
                                    name: assessment.content.interviewName
                                });
                            }
                        } catch (error) {
                            console.error("Failed to fetch assessment details:", error);
                            // Set with ID only if fetch fails
                            setSelectedInterviewAssessment({
                                type: "Interview",
                                id: data.assessment_id,
                                name: data.assessment_id
                            });
                        }
                    }
                }
            });
        }
    }, [props.isEditMode, alias]);

    // Auto-generate slug from company name
    const generateSlug = (name: string) => {
        return name
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/(^-|-$)/g, "");
    };

    const handleCompanyNameChange = (value: string) => {
        setFormData((prev) => ({
            ...prev,
            company_name: value,
            alias: generateSlug(value),
        }));
    };

    const copyDemoLink = async () => {
        const fullUrl = `${import.meta.env.VITE_TALENT_AI_APP}/demo/${formData.alias}`;
        try {
            await navigator.clipboard.writeText(fullUrl);
            setIsCopied(true);
            snackbar.showSuccess("Demo link copied to clipboard!");
            setTimeout(() => setIsCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy: ', err);
            snackbar.showError("Failed to copy link to clipboard.");
        }
    };

    const handleFileUpload = (file: File, type: "logoA" | "logoB") => {
        if (
            file &&
            (file.type.includes("image/png") ||
                file.type.includes("image/svg") ||
                file.type.includes("image/jpeg") ||
                file.type.includes("image/jpg"))
        ) {
            const reader = new FileReader();
            reader.onload = (e) => {
                if (type === "logoA") {
                    setLogoAPreviews(e.target?.result as string);
                    setCompanyLogoInterviewFile(file);
                } else {
                    setLogoBPreviews(e.target?.result as string);
                    setCompanyLogoFile(file);
                }
            };
            reader.readAsDataURL(file);

            setFormData((prev) => ({
                ...prev,
                [type]: file,
            }));
        }
    };

    const removeFile = (type: "logoA" | "logoB") => {
        setFormData((prev) => ({
            ...prev,
            [type]: null,
        }));
        if (type === "logoA") {
            setLogoAPreviews(null);
            setCompanyLogoInterviewFile(null);
        } else {
            setLogoBPreviews(null);
            setCompanyLogoFile(null);
        }
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        createUpdateDemoInterview({
            ...formData,
            company_logo: companyLogoFile,
            company_logo_interview: companyLogoInterviewFile,
            assessment_id: selectedInterviewAssessment.id || undefined,
        }).then((resp) => {
            if (resp.status === 201) {
                snackbar.showSuccess("Demo link saved successfully!");
            }
        }).catch((err) => {
            console.error(err);
            snackbar.showError("Failed to save demo link. Please try again.");
        });
    };

    const FileUploadArea = ({
        type,
        label,
        preview,
        onUpload,
        onRemove,
    }: {
        type: "logoA" | "logoB";
        label: string;
        preview: string | null;
        onUpload: (file: File) => void;
        onRemove: () => void;
    }) => (
        <div className="space-y-2">
            <div className="flex items-center gap-2">
                <Label htmlFor={type} className="text-sm font-medium">
                    {label}
                </Label>
            </div>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-gray-400 transition-colors">
                {preview ? (
                    <div className="relative">
                        <img
                            src={preview}
                            alt={`${type} preview`}
                            className="w-full h-32 object-contain rounded"
                        />

                        <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            className="absolute top-2 right-2 h-8 w-8 p-0"
                            onClick={onRemove}
                        >
                            <XIcon className="h-4 w-4" />
                        </Button>
                    </div>
                ) : (
                    <label
                        htmlFor={type}
                        className="flex flex-col items-center justify-center h-32 cursor-pointer"
                    >
                        <UploadIcon className="h-12 w-12 text-gray-400 mb-3" />

                        <span className="text-sm font-medium text-gray-700 mb-1">
                            Upload PNG, SVG, or JPG
                        </span>
                        <span className="text-xs text-gray-500">Max 5MB</span>
                    </label>
                )}
                <input
                    id={type}
                    type="file"
                    accept=".png,.svg,.jpg,.jpeg"
                    className="hidden"
                    onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) onUpload(file);
                    }}
                />
            </div>
        </div>
    );

    return (
        <div className="bg-white">
            <form onSubmit={handleSubmit} className="space-y-8">
                {/* Company Information */}
                <div className="space-y-6">
                    <h2 className="text-xl font-semibold text-gray-900">
                        Company Information
                    </h2>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="space-y-2">
                            <Label
                                htmlFor="companyName"
                                className="text-sm font-medium"
                            >
                                Company Name
                            </Label>
                            <Input
                                id="companyName"
                                value={formData.company_name}
                                onChange={(e) => handleCompanyNameChange(e.target.value)}
                                placeholder="e.g., TechCorp Solutions"
                                required
                                className="h-12"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="alias" className="text-sm font-medium">
                                Alias (URL Slug)
                            </Label>
                            <div className="flex items-center">
                                <div className="bg-gray-100 border border-r-0 border-gray-300 rounded-l-md px-3 h-12 flex items-center text-sm text-gray-600 whitespace-nowrap">
                                    app.ourteam.ai/demo/
                                </div>
                                <Input
                                    id="alias"
                                    value={formData.alias}
                                    onChange={(e) =>
                                        setFormData((prev) => ({
                                            ...prev,
                                            alias: e.target.value,
                                        }))
                                    }
                                    placeholder="e.g., techcorp-solutions"
                                    className="h-12 rounded-none border-l-0 border-r-0 focus:border-l focus:border-r"
                                />
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    className="h-12 rounded-l-none border-l-0 px-3"
                                    onClick={copyDemoLink}
                                    disabled={!formData.alias}
                                    title="Copy demo link"
                                >
                                    <CopyIcon className="h-4 w-4" />
                                </Button>
                            </div>

                            <p className="text-xs text-gray-500">
                                Auto-generated from company name if left empty.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Logo Uploads */}
                <div className="space-y-6">
                    <div className="flex items-center justify-between">
                        <h2 className="text-xl font-semibold text-gray-900">
                            Logo Assets
                        </h2>
                        <DemoPreviewDialog
                            open={previewOpen}
                            onOpenChange={setPreviewOpen}
                            logoAPreviews={logoAPreviews}
                            logoBPreviews={logoBPreviews}
                            companyName={formData.company_name}
                            companyColor={formData.company_color}
                        >
                            <Button
                                type="button"
                                variant="outline"
                                className="flex items-center gap-2"
                            >
                                <EyeIcon className="h-4 w-4" />
                                Preview Demo
                            </Button>
                        </DemoPreviewDialog>
                    </div>
                    <div className="space-y-6">
                        {/* Logo Upload Areas */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                            <FileUploadArea
                                type="logoA"
                                label="Landing Page"
                                preview={logoAPreviews}
                                onUpload={(file) => handleFileUpload(file, "logoA")}
                                onRemove={() => removeFile("logoA")}
                            />

                            <FileUploadArea
                                type="logoB"
                                label="Interview Screen"
                                preview={logoBPreviews}
                                onUpload={(file) => handleFileUpload(file, "logoB")}
                                onRemove={() => removeFile("logoB")}
                            />
                        </div>
                    </div>
                </div>

                {/* Interview Assessment */}
                <div className="space-y-6">
                    <h2 className="text-xl font-semibold text-gray-900">
                        AI Interview
                    </h2>
                    <div className="space-y-4">
                        <div className="flex items-center gap-4">
                            <div className="flex-1">
                                <Label htmlFor="interview" className="text-base font-medium mb-2 block">
                                    Select Interview
                                </Label>
                                <Select
                                    value={(() => {
                                        // console.log("Select value:", selectedInterviewAssessment.id || "None", "Selected assessment:", selectedInterviewAssessment);
                                        return selectedInterviewAssessment.id || "None";
                                    })()}
                                    onValueChange={(value: string) => {
                                        console.log("onValueChange triggered with:", value);
                                        if (value === "None") {
                                            setSelectedInterviewAssessment({ type: "", id: "", name: "None" });
                                        } else {
                                            const selectedAssessment = assessments.find((item) => item.type === "Interview" && (item as InterviewAssessment).id === value) as InterviewAssessment;
                                            console.log("Found assessment in list:", selectedAssessment);
                                            if (selectedAssessment) {
                                                setSelectedInterviewAssessment({
                                                    type: "Interview",
                                                    id: value,
                                                    name: selectedAssessment.content.interviewName
                                                });
                                            } else {
                                                // Don't update if assessment not found - keep existing selection
                                                console.warn("Assessment not found in list, keeping existing selection");
                                            }
                                        }
                                    }}
                                >
                                    <SelectTrigger className="w-full">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="None">None</SelectItem>
                                        {assessments.filter((item) => item.type === "Interview").map((assessment, index) => (
                                            <SelectItem key={index} value={(assessment as InterviewAssessment).id || ""}>
                                                {(assessment as InterviewAssessment).content.interviewName}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="pt-8">
                                <Button
                                    variant="outline"
                                    className="flex items-center gap-2 text-blue-600 border-blue-200 hover:bg-blue-50"
                                    onClick={() => window.open('/assess', '_blank')}
                                    type="button"
                                >
                                    <PlusIcon className="h-4 w-4" />
                                    Create new interview
                                </Button>
                            </div>
                            <div className="pt-8">
                                <Button
                                    variant="outline"
                                    onClick={loadAssessment}
                                    type="button"
                                >
                                    <RefreshCcw className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                        <p className="text-sm text-gray-500">
                            Select an AI interview for the demo. If 'None' is selected, a default AI interview will be used.
                        </p>
                    </div>
                </div>

                {/* Settings */}
                <div className="space-y-6">
                    <h2 className="text-xl font-semibold text-gray-900">
                        Demo Settings
                    </h2>
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* Color Scheme */}
                        <div className="space-y-2">
                            <Label
                                htmlFor="colorScheme"
                                className="text-sm font-medium"
                            >
                                Color Scheme
                            </Label>
                            <div className="flex items-center gap-3">
                                <div className="relative">
                                    <input
                                        id="colorScheme"
                                        type="color"
                                        value={formData.company_color}
                                        onChange={(e) =>
                                            setFormData((prev) => ({
                                                ...prev,
                                                company_color: e.target.value,
                                            }))
                                        }
                                        className="w-12 h-12 rounded border border-gray-300 cursor-pointer"
                                    />
                                </div>
                                <Input
                                    value={formData.company_color}
                                    onChange={(e) =>
                                        setFormData((prev) => ({
                                            ...prev,
                                            company_color: e.target.value,
                                        }))
                                    }
                                    placeholder="#2863EB"
                                    className="flex-1 h-12"
                                    pattern="^#[0-9A-Fa-f]{6}$"
                                />
                            </div>
                            <p className="text-xs text-gray-500">
                                Primary brand color in HEX format
                            </p>
                        </div>

                        {/* Expiry Settings */}
                        {/* <div className="space-y-2 hidden">
                            <Label htmlFor="expiry" className="text-sm font-medium">
                                Demo Expiry
                            </Label>
                            <Select
                                value={formData.expiry}
                                onValueChange={(value) =>
                                    setFormData((prev) => ({ ...prev, expiry: value }))
                                }
                            >
                                <SelectTrigger className="h-12">
                                    <SelectValue placeholder="Select expiry period" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="30">30 days (Default)</SelectItem>
                                    <SelectItem value="60">60 days</SelectItem>
                                    <SelectItem value="90">90 days (Maximum)</SelectItem>
                                </SelectContent>
                            </Select>
                            <p className="text-xs text-gray-500">
                                Demo link will automatically expire after this period
                            </p>
                        </div> */}

                        {/* Created By */}
                        {/* <div className="space-y-2 hidden">
                            <Label htmlFor="createdBy" className="text-sm font-medium">
                                Created By
                            </Label>
                            <Input
                                id="createdBy"
                                value={formData.createdBy}
                                onChange={(e) =>
                                    setFormData((prev) => ({
                                        ...prev,
                                        createdBy: e.target.value,
                                    }))
                                }
                                placeholder="e.g., Sarah Johnson"
                                required
                                className="h-12"
                            />

                            <p className="text-xs text-gray-500">
                                Name of the person creating this demo
                            </p>
                        </div> */}
                    </div>
                </div>

                {/* Form Actions */}
                <div className="flex items-center justify-end gap-4 pt-6 border-t border-gray-200">
                    <Link to="/demo">
                        <Button type="button" variant="outline" size="lg">
                            Cancel
                        </Button>
                    </Link>
                    <Button
                        type="submit"
                        className="bg-blue-600 hover:bg-blue-700"
                        disabled={!formData.company_name || !formData.alias}
                        size="lg"
                        style={{ paddingRight: "24px", paddingLeft: "24px" }}
                    >
                        {props.isEditMode ? "Save Changes" : "Create Demo Link"}
                    </Button>
                </div>
            </form>
        </div>
    )
}